import { createApp as createVueApp, createSSRApp, h } from 'vue';
import { SitecoreJssPlaceholderPlugin } from '@sitecore-jss/sitecore-jss-vue';
import {
  createMetaManager as createVueMetaManager,
  deepestResolver,
  defaultConfig,
} from 'vue-meta';
import AppRoot from './AppRoot';
import { createRouter } from './router';
import SitecoreJssStorePlugin from './lib/SitecoreJssStorePlugin';
import componentFactory from './temp/componentFactory';
import directives from '@/directives';
import elements from './elements';
import sections from './sections';
import formElements from './form';
import EventBus from '@/utils/bus';
import { createPinia } from 'pinia';
import { canUseDOM } from '@/utils/dom-utils';
import { formatDate, formatOrderDate, getLastDayOfCurrentMonth } from '@/utils/date-utils';
import { isNoE, padding } from '@/utils/obj-utils';
import {
  appVisible,
  buildAddress,
  checkLangOfUrl,
  formatModel,
  formDdl,
  getAdaptiveField,
  getAdaptiveImageField,
  getAdaptiveTextField,
  getAdaptiveValueField,
  getAdpRatio,
  getPageAlpha2Code,
  gridLayoutClass,
  handleComClass,
  isOnApp,
  settingVal,
  settingValue,
  tryField,
  tryValueField,
} from '@/utils/site-utils';
import { concatString, eq, formatString, ifEmpty, toCapitalize } from '@/utils/string-utils';
import spinners from '@/spinners';
import { formatMoney, sum, toThousands } from '@/utils/math-utils';
import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination, Thumbs, EffectFade, Keyboard } from 'swiper/modules';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import { merge } from 'lodash-es';
import { getBetterUrl, getLangFromUrl, redirectTo } from '@/utils/uri-utils';
import useDevice from '@/hooks/useDevice';
import scriptLoaderService from '@/services/scriptLoaderService';
import useAppStore from '@/store/appStore';

const createMetaManager = (isSSR = false) => {
  const options = merge(defaultConfig, {
    meta: {
      nameless: true,
      tagIDKeyName: 'vmid',
      attribute: 'data-vue-meta',
      ssrAppId: 'app',
      ssrAttribute: 'data-vue-meta-server-rendered',
      refreshOnceOnNavigation: true,
    },
  });
  return createVueMetaManager(isSSR, options, deepestResolver);
};
// createApp is invoked by both the main and SSR entry points, so the two entry points can use the same app creation process.
export function createApp(initialState, i18n, isSSR) {
  const router = createRouter(isSSR);
  const metaManager = createMetaManager(isSSR);
  const { deviceState, deviceComputes } = useDevice();
  // const graphQLProvider = createGraphQLProvider(initialState);

  const vueOptions = {
    router,
    render: () => h(AppRoot),
    i18n,
  };
  const app = isSSR ? createSSRApp(vueOptions) : createVueApp(vueOptions);
  const $bus = new EventBus();
  // app.provide(DefaultApolloClient, graphQLProvider);

  app.use(router);
  app.use(SitecoreJssStorePlugin);
  app.use(SitecoreJssPlaceholderPlugin, { componentFactory });
  app.use(i18n);
  app.use(metaManager);
  app.use(directives);
  app.use(elements);
  app.use(sections);
  app.use(formElements);
  app.use(spinners);
  app.provide('$bus', $bus);
  app.provide('device-common', { deviceState, deviceComputes });
  app.provide('scriptLoader', scriptLoaderService);
  app.config.globalProperties.$bus = $bus;
  app.config.globalProperties.$deviceState = deviceState;
  app.config.globalProperties.$deviceComputes = deviceComputes;
  app.config.globalProperties.$isNoE = isNoE;
  app.config.globalProperties.$formatDate = formatDate;
  app.config.globalProperties.$settingValue = settingValue;
  app.config.globalProperties.$settingVal = settingVal;
  app.config.globalProperties.$formatString = formatString;
  app.config.globalProperties.$handleComClass = handleComClass;
  app.config.globalProperties.$padding = padding;
  app.config.globalProperties.$concatString = concatString;
  app.config.globalProperties.$buildAddress = buildAddress;
  app.config.globalProperties.$buildHref = getBetterUrl;
  app.config.globalProperties.$formatMoney = formatMoney;
  app.config.globalProperties.$eq = eq;
  app.config.globalProperties.$ifEmpty = ifEmpty;
  app.config.globalProperties.$tryField = tryField;
  app.config.globalProperties.$tryValueField = tryValueField;
  app.config.globalProperties.$toCapitalize = toCapitalize;
  app.config.globalProperties.$sum = sum;
  app.config.globalProperties.$toThousands = toThousands;
  app.config.globalProperties.$redirectTo = (url, target) => {
    redirectTo({ url, target, router });
  };
  app.config.globalProperties.$formatOrderDate = formatOrderDate;
  app.config.globalProperties.$adaptiveField = (
    desktopField,
    mobileField,
    tabletField = null,
    desktopLargeField = null
  ) => {
    return getAdaptiveField(deviceState, desktopField, mobileField, tabletField, desktopLargeField);
  };
  app.config.globalProperties.$adaptiveValueField = (
    desktopField,
    mobileField,
    tabletField,
    desktopLargeField,
    valueFn
  ) => {
    return getAdaptiveValueField(
      deviceState,
      desktopField,
      mobileField,
      tabletField,
      desktopLargeField,
      valueFn
    );
  };
  app.config.globalProperties.$adaptiveTextField = (
    desktopField,
    mobileField,
    tabletField = null,
    desktopLargeField = null
  ) => {
    return getAdaptiveTextField(
      deviceState,
      desktopField,
      mobileField,
      tabletField,
      desktopLargeField
    );
  };
  app.config.globalProperties.$adaptiveImageField = (
    desktopField,
    mobileField,
    tabletField = null,
    desktopLargeField = null
  ) => {
    return getAdaptiveImageField(
      deviceState,
      desktopField,
      mobileField,
      tabletField,
      desktopLargeField
    );
  };
  app.config.globalProperties.$apdRatio = (ratioItem) => {
    return getAdpRatio(deviceState, ratioItem);
  };
  app.config.globalProperties.$gridLayoutClass = gridLayoutClass;
  app.config.globalProperties.$isOnApp = isOnApp;
  app.config.globalProperties.$appVisible = appVisible;
  app.config.globalProperties.$equalAlpha2Code = ($route, alpha2Code) => {
    return eq(getPageAlpha2Code($route), alpha2Code);
  };
  app.config.globalProperties.$formatModel = formatModel;
  app.config.globalProperties.$getLastDayOfCurrentMonth = getLastDayOfCurrentMonth;
  app.config.globalProperties.$formDdl = formDdl;
  // Wrap '&' characters in <span style="font-family: unset;"> for any v-html binding.
  // Usage in templates: v-html="$wrapAmp(field.value)"
  if (canUseDOM()) {
    checkLangOfUrl();
    const pinia = createPinia();
    app.use(pinia);

    // 同步初始化 appStore.lang，避免子组件 API 调用早于 RouteHandler 异步 setLang 导致 lang fallback 到 'en'
    const appStore = useAppStore();
    if (!appStore.lang) {
      appStore.setLang(getLangFromUrl());
    }

    Swiper.use([Autoplay, Pagination, Navigation, Thumbs, EffectFade, Keyboard]); //,EffectFade
    gsap.registerPlugin(ScrollTrigger);
  }
  // if there is an initial state defined, push it into the store, where it can be referenced by interested components.
  if (initialState) {
    app.config.globalProperties.$jss.store.setSitecoreData(initialState);
  }

  return { app, router /*, graphQLProvider*/ };
}

export function createGraphQLProvider(initialState) {
  return null;
  // return initialState && initialState.APOLLO_STATE ? GraphQLClientFactory(config.graphQLEndpoint, false, initialState.APOLLO_STATE) : GraphQLClientFactory(config.graphQLEndpoint, true);
}
